import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Heart, Images, X, ChevronLeft, ChevronRight, Download, ArrowLeft, ImageIcon, Loader2, RefreshCw, Share2, Play, Film } from 'lucide-react';
import { PublicLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { getWeddingPhotos, uploadWeddingPhoto, likeWeddingPhoto, photoFileUrl, getAuthToken, albumAccess } from '@/lib/api';

// ===================== ACCÈS ALBUM (email seul) =====================

const AlbumAccessForm = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await albumAccess(email);
      localStorage.setItem('guest_token', res.data.token);
      toast.success('Bienvenue dans l\'album !');
      onSuccess();
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Accès interdit : cet email ne figure pas dans la liste des invités");
      } else {
        toast.error('Erreur, veuillez réessayer');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="card-elegant max-w-md mx-auto">
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-charcoal mb-2">Votre adresse email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="votre@email.fr"
              required
              autoComplete="email"
              data-testid="album-access-email-input"
            />
          </div>
          <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="album-access-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Images className="w-4 h-4 mr-2" />}
            Accéder à l'album
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

const isHeic = (file) => /heic|heif/i.test(file.type) || /\.(heic|heif)$/i.test(file.name);
const isVideoFile = (file) => /^video\//i.test(file.type) || /\.(mp4|mov|webm|m4v)$/i.test(file.name);
const MAX_SIZE = 25 * 1024 * 1024;
const MAX_VIDEO_SIZE = 300 * 1024 * 1024;

// ===================== ACCUEIL PHOTOS =====================

const HomeView = ({ onUpload, onAlbum }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto text-center">
    <h1 className="font-display text-4xl sm:text-5xl text-charcoal mb-4">
      Nos souvenirs <Heart className="inline w-8 h-8 text-terracotta fill-terracotta -mt-2" />
    </h1>
    <p className="text-stone text-base md:text-lg mb-10 max-w-lg mx-auto">
      Partagez vos plus belles photos du mariage et découvrez celles déposées par nos invités.
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
      <Card className="card-elegant hover:shadow-lg cursor-pointer" onClick={onUpload} data-testid="photos-home-upload-card">
        <CardContent className="p-4 flex flex-col items-center">
          <div className="w-20 h-20 rounded-full bg-terracotta/10 flex items-center justify-center mb-4">
            <Camera className="w-10 h-10 text-terracotta" />
          </div>
          <h2 className="font-display text-lg text-charcoal mb-2">Partager mes photos</h2>
          <Button className="btn-primary w-full mt-2" data-testid="photos-upload-btn">Déposer mes photos</Button>
        </CardContent>
      </Card>
      <Card className="card-elegant hover:shadow-lg cursor-pointer" onClick={onAlbum} data-testid="photos-home-album-card">
        <CardContent className="p-4 flex flex-col items-center">
          <div className="w-20 h-20 rounded-full bg-gold/10 flex items-center justify-center mb-4">
            <Heart className="w-10 h-10 text-gold-dark" />
          </div>
          <h2 className="font-display text-lg text-charcoal mb-2">Voir les photos</h2>
          <Button variant="outline" className="btn-secondary w-full mt-2" data-testid="photos-album-btn">Découvrir l'album</Button>
        </CardContent>
      </Card>
    </div>
    <p className="text-stone text-sm mt-8">Le dépôt de photos est libre. L'accès à l'album demande simplement votre email d'invitation.</p>
  </motion.div>
);

// ===================== DÉPÔT DE PHOTOS =====================

const UploadView = ({ onBack, onAlbum }) => {
  const [items, setItems] = useState([]); // {key, file, preview, status: 'pending'|'sending'|'done'|'error'}
  const [phase, setPhase] = useState('select'); // select | sending | done
  const [sentCount, setSentCount] = useState(0);
  const [uploaderName, setUploaderName] = useState(() => localStorage.getItem('photos_uploader_name') || '');
  const inputRef = useRef(null);

  const handleSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = [];
    for (const f of files) {
      const video = isVideoFile(f);
      if (f.size > (video ? MAX_VIDEO_SIZE : MAX_SIZE)) {
        toast.error(`${f.name} dépasse ${video ? '300' : '25'} Mo et a été ignorée`);
        continue;
      }
      valid.push({
        key: `${f.name}-${f.size}-${Date.now()}-${Math.random()}`,
        file: f,
        isVideo: video,
        preview: isHeic(f) || video ? null : URL.createObjectURL(f),
        status: 'pending',
      });
    }
    setItems((prev) => [...prev, ...valid]);
    e.target.value = '';
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const sendAll = async () => {
    setPhase('sending');
    if (uploaderName.trim()) localStorage.setItem('photos_uploader_name', uploaderName.trim());
    const toSend = items.filter((i) => i.status !== 'done');
    let done = items.filter((i) => i.status === 'done').length;
    let errors = 0;
    for (const item of toSend) {
      setItems((prev) => prev.map((i) => (i.key === item.key ? { ...i, status: 'sending' } : i)));
      try {
        await uploadWeddingPhoto(item.file, uploaderName.trim());
        done += 1;
        setSentCount(done);
        setItems((prev) => prev.map((i) => (i.key === item.key ? { ...i, status: 'done' } : i)));
      } catch (err) {
        errors += 1;
        setItems((prev) => prev.map((i) => (i.key === item.key ? { ...i, status: 'error' } : i)));
      }
    }
    if (errors > 0) {
      toast.error(`${errors} photo${errors > 1 ? 's' : ''} n'ont pas pu être envoyées`);
      setPhase('select');
    } else {
      setPhase('done');
    }
  };

  const resetForMore = () => {
    setItems([]);
    setSentCount(0);
    setPhase('select');
  };

  const pendingCount = items.filter((i) => i.status !== 'done').length;
  const totalToSend = items.length;
  const hasErrors = items.some((i) => i.status === 'error');

  if (phase === 'done') {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md mx-auto text-center">
        <div className="w-24 h-24 rounded-full bg-terracotta/10 flex items-center justify-center mx-auto mb-6">
          <Heart className="w-12 h-12 text-terracotta fill-terracotta" />
        </div>
        <h1 className="font-display text-4xl text-charcoal mb-4">Merci <Heart className="inline w-7 h-7 text-terracotta fill-terracotta -mt-2" /></h1>
        <p className="text-charcoal text-lg mb-2" data-testid="photos-upload-success-count">
          Vous venez de déposer {sentCount} photo{sentCount > 1 ? 's' : ''} !
        </p>
        <p className="text-stone mb-8">Merci de contribuer à tous les souvenirs de cette magnifique journée.</p>
        <div className="flex flex-col gap-3">
          <Button className="btn-primary w-full" onClick={resetForMore} data-testid="photos-add-more-btn">
            Ajouter d'autres photos
          </Button>
          <Button variant="outline" className="btn-secondary w-full" onClick={onAlbum} data-testid="photos-see-album-btn">
            Voir l'album
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto">
      <button onClick={onBack} className="flex items-center gap-2 text-stone hover:text-terracotta mb-6 text-sm" data-testid="photos-upload-back-btn">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>
      <div className="text-center mb-8">
        <h1 className="font-display text-3xl sm:text-4xl text-charcoal mb-4">
          Partagez vos plus beaux souvenirs avec nous <Heart className="inline w-6 h-6 text-terracotta fill-terracotta -mt-1" />
        </h1>
        <p className="text-stone max-w-lg mx-auto">
          Vous avez capturé un sourire, un fou rire, une danse improbable ou simplement un joli moment de notre mariage ?
          Envoyez-nous vos plus belles photos ! Elles viendront compléter les souvenirs de cette journée inoubliable.
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,video/mp4,video/quicktime,video/webm,.jpg,.jpeg,.png,.webp,.heic,.heif,.mp4,.mov,.webm,.m4v"
        className="hidden"
        onChange={handleSelect}
        data-testid="photos-file-input"
      />

      {phase === 'sending' ? (
        <div className="text-center py-10">
          <Loader2 className="w-10 h-10 text-terracotta animate-spin mx-auto mb-4" />
          <p className="font-display text-xl text-charcoal mb-2">Envoi de vos souvenirs...</p>
          <p className="text-stone" data-testid="photos-upload-progress">
            {sentCount} / {totalToSend} photo{totalToSend > 1 ? 's' : ''} envoyée{sentCount > 1 ? 's' : ''}
          </p>
          <div className="w-full max-w-xs mx-auto bg-terracotta-light/40 rounded-full h-2 mt-4">
            <div className="bg-terracotta h-2 rounded-full transition-all" style={{ width: `${totalToSend ? (sentCount / totalToSend) * 100 : 0}%` }} />
          </div>
        </div>
      ) : (
        <>
          <div className="max-w-sm mx-auto mb-6">
            <label className="block text-sm font-medium text-charcoal mb-2 text-center">Votre prénom (facultatif)</label>
            <Input
              value={uploaderName}
              onChange={(e) => setUploaderName(e.target.value)}
              placeholder="Pour que les mariés sachent qui a partagé"
              className="text-center"
              maxLength={60}
              data-testid="photos-uploader-name-input"
            />
          </div>
          <div className="text-center mb-6">
            <Button className="btn-primary text-base py-6 !px-6 w-full max-w-xs" onClick={() => inputRef.current?.click()} data-testid="photos-select-btn">
              <Camera className="w-5 h-5 mr-2" /> Sélectionner mes photos
            </Button>
            <p className="text-xs text-stone mt-3">Photos et vidéos acceptées (vidéos jusqu'à 300 Mo)</p>
          </div>

          {items.length > 0 && (
            <>
              <p className="text-center text-charcoal font-medium mb-4" data-testid="photos-selected-count">
                {items.length} photo{items.length > 1 ? 's' : ''} sélectionnée{items.length > 1 ? 's' : ''}
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-6">
                {items.map((item) => (
                  <div key={item.key} className={`relative aspect-square rounded-xl overflow-hidden bg-terracotta-light/30 ${item.status === 'error' ? 'ring-2 ring-red-400' : ''} ${item.status === 'done' ? 'opacity-50' : ''}`}>
                    {item.preview ? (
                      <img src={item.preview} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-stone p-1">
                        {item.isVideo ? <Film className="w-6 h-6 mb-1" /> : <ImageIcon className="w-6 h-6 mb-1" />}
                        <span className="text-[10px] truncate w-full text-center">{item.isVideo ? 'Vidéo' : item.file.name}</span>
                      </div>
                    )}
                    {item.status !== 'done' && (
                      <button
                        onClick={() => removeItem(item.key)}
                        className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center"
                        data-testid="photos-remove-item-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    {item.status === 'error' && (
                      <span className="absolute bottom-0 inset-x-0 bg-red-500/90 text-white text-[10px] text-center py-0.5">Échec</span>
                    )}
                  </div>
                ))}
              </div>
              <Button className="btn-primary w-full text-base py-6" onClick={sendAll} disabled={pendingCount === 0} data-testid="photos-send-btn">
                {hasErrors ? (<><RefreshCw className="w-5 h-5 mr-2" /> Réessayer les envois échoués</>) : 'Envoyer mes photos'}
              </Button>
            </>
          )}
        </>
      )}
    </motion.div>
  );
};

// ===================== ALBUM =====================

const LikeButton = ({ photo, onToggle, size = 'sm' }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onToggle(photo.id); }}
    className={`flex items-center gap-1 rounded-full transition-colors ${
      size === 'sm'
        ? 'bg-black/50 text-white px-2 py-1 text-xs'
        : 'bg-white/10 text-white px-3 py-2 text-sm'
    }`}
    data-testid="photos-like-btn"
  >
    <Heart className={`${size === 'sm' ? 'w-3.5 h-3.5' : 'w-5 h-5'} ${photo.liked_by_me ? 'fill-terracotta text-terracotta' : ''}`} />
    {photo.likes_count > 0 && <span data-testid="photos-like-count">{photo.likes_count}</span>}
  </button>
);

const Lightbox = ({ photos, index, onClose, onNavigate, onToggleLike }) => {
  const touchStart = useRef(null);
  const [sharing, setSharing] = useState(false);
  const photo = photos[index];

  const sharePhoto = async () => {
    if (sharing) return;
    setSharing(true);
    const video = photo.media_type === 'video';
    try {
      const res = await fetch(photoFileUrl(photo.id));
      const blob = await res.blob();
      const file = new File([blob], video ? 'video-mariage-melanie-cedric.mp4' : 'photo-mariage-melanie-cedric.jpg',
        { type: blob.type || (video ? 'video/mp4' : 'image/jpeg') });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Mariage Mélanie & Cédric',
          text: 'Une photo du mariage de Mélanie & Cédric ❤',
        });
      } else if (navigator.share) {
        await navigator.share({
          title: 'Mariage Mélanie & Cédric',
          text: 'Découvrez les photos du mariage de Mélanie & Cédric ❤',
          url: `${window.location.origin}/photos`,
        });
      } else {
        toast.info('Partage non disponible sur cet appareil — utilisez le bouton de téléchargement');
      }
    } catch (e) {
      if (e.name !== 'AbortError') toast.error('Partage impossible, réessayez');
    } finally {
      setSharing(false);
    }
  };

  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft' && index > 0) onNavigate(index - 1);
    if (e.key === 'ArrowRight' && index < photos.length - 1) onNavigate(index + 1);
  }, [index, photos.length, onClose, onNavigate]);

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  if (!photo) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" data-testid="photos-lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 z-10 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center" data-testid="photos-lightbox-close">
        <X className="w-6 h-6" />
      </button>
      <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
        <a
          href={photoFileUrl(photo.id, { download: true })}
          className="w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center"
          data-testid="photos-lightbox-download"
        >
          <Download className="w-5 h-5" />
        </a>
        <button
          onClick={sharePhoto}
          className="w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center"
          data-testid="photos-lightbox-share"
        >
          {sharing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Share2 className="w-5 h-5" />}
        </button>
        <LikeButton photo={photo} onToggle={onToggleLike} size="lg" />
      </div>
      {index > 0 && (
        <button onClick={() => onNavigate(index - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center" data-testid="photos-lightbox-prev">
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}
      {index < photos.length - 1 && (
        <button onClick={() => onNavigate(index + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center" data-testid="photos-lightbox-next">
          <ChevronRight className="w-6 h-6" />
        </button>
      )}
      {photo.media_type === 'video' ? (
        <video
          key={photo.id}
          src={photoFileUrl(photo.id)}
          poster={photoFileUrl(photo.id, { thumb: true })}
          controls
          autoPlay
          playsInline
          className="max-w-full max-h-[80vh] object-contain"
          data-testid="photos-lightbox-video"
        />
      ) : (
      <img
        src={photoFileUrl(photo.id)}
        alt="Photo du mariage"
        className="max-w-full max-h-full object-contain select-none"
        onTouchStart={(e) => { touchStart.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchStart.current === null) return;
          const delta = e.changedTouches[0].clientX - touchStart.current;
          if (delta > 60 && index > 0) onNavigate(index - 1);
          if (delta < -60 && index < photos.length - 1) onNavigate(index + 1);
          touchStart.current = null;
        }}
      />
      )}
      <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">{index + 1} / {photos.length}</span>
    </div>
  );
};

const AlbumView = ({ onBack, onUpload, onAuthNeeded }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [sortBy, setSortBy] = useState('recent'); // recent | top

  const sortedPhotos = sortBy === 'top'
    ? [...photos].sort((a, b) => (b.likes_count - a.likes_count) || (new Date(b.created_at) - new Date(a.created_at)))
    : photos;

  useEffect(() => {
    getWeddingPhotos()
      .then((res) => setPhotos(res.data || []))
      .catch((err) => {
        if (err.response?.status === 401) onAuthNeeded();
        else toast.error("Impossible de charger l'album");
      })
      .finally(() => setLoading(false));
  }, [onAuthNeeded]);

  const toggleLike = async (photoId) => {
    setPhotos((prev) => prev.map((p) => p.id === photoId
      ? { ...p, liked_by_me: !p.liked_by_me, likes_count: p.likes_count + (p.liked_by_me ? -1 : 1) }
      : p));
    try {
      const res = await likeWeddingPhoto(photoId);
      setPhotos((prev) => prev.map((p) => p.id === photoId
        ? { ...p, liked_by_me: res.data.liked, likes_count: res.data.likes_count }
        : p));
    } catch (e) {
      setPhotos((prev) => prev.map((p) => p.id === photoId
        ? { ...p, liked_by_me: !p.liked_by_me, likes_count: p.likes_count + (p.liked_by_me ? -1 : 1) }
        : p));
      toast.error('Erreur, réessayez');
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <button onClick={onBack} className="flex items-center gap-2 text-stone hover:text-terracotta text-sm" data-testid="photos-album-back-btn">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>
        <h1 className="font-display text-2xl sm:text-3xl text-charcoal">L'album des invités</h1>
        <span className="text-sm text-stone hidden sm:block">{photos.length} photo{photos.length > 1 ? 's' : ''}</span>
      </div>

      {photos.length > 1 && (
        <div className="flex items-center justify-center gap-2 mb-6" data-testid="photos-sort-toggle">
          <button
            onClick={() => setSortBy('recent')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${sortBy === 'recent' ? 'bg-terracotta text-white' : 'bg-terracotta/10 text-terracotta hover:bg-terracotta/20'}`}
            data-testid="photos-sort-recent-btn"
          >
            Récentes
          </button>
          <button
            onClick={() => setSortBy('top')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${sortBy === 'top' ? 'bg-terracotta text-white' : 'bg-terracotta/10 text-terracotta hover:bg-terracotta/20'}`}
            data-testid="photos-sort-top-btn"
          >
            <Heart className={`w-3.5 h-3.5 ${sortBy === 'top' ? 'fill-white' : 'fill-terracotta'}`} /> Top photos
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16"><Loader2 className="w-8 h-8 text-terracotta animate-spin mx-auto" /></div>
      ) : photos.length === 0 ? (
        <div className="text-center py-16">
          <Images className="w-12 h-12 text-stone/40 mx-auto mb-4" />
          <p className="text-stone mb-6">Aucune photo pour le moment. Soyez le premier à partager un souvenir !</p>
          <Button className="btn-primary" onClick={onUpload} data-testid="photos-album-empty-upload-btn">
            <Camera className="w-4 h-4 mr-2" /> Ajouter mes photos
          </Button>
        </div>
      ) : (
        <div className="columns-2 sm:columns-3 lg:columns-4 gap-2 sm:gap-3 pb-24" data-testid="photos-album-grid">
          {sortedPhotos.map((p, i) => (
            <div key={p.id} className="relative mb-2 sm:mb-3 break-inside-avoid">
              <img
                src={photoFileUrl(p.id, { thumb: true })}
                alt="Souvenir du mariage"
                loading="lazy"
                onClick={() => setLightboxIndex(i)}
                className="w-full rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
                data-testid="photos-album-item"
              />
              {p.media_type === 'video' && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
                    <Play className="w-6 h-6 text-white fill-white ml-0.5" />
                  </span>
                </div>
              )}
              <div className="absolute bottom-2 right-2">
                <LikeButton photo={p} onToggle={toggleLike} />
              </div>
            </div>
          ))}
        </div>
      )}

      {photos.length > 0 && (
        <button
          onClick={onUpload}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-terracotta hover:bg-[#C39365] text-white rounded-full px-6 py-3.5 shadow-xl flex items-center gap-2 text-sm font-medium uppercase tracking-wide"
          data-testid="photos-album-add-btn"
        >
          <Camera className="w-5 h-5" /> Ajouter mes photos
        </button>
      )}

      <AnimatePresence>
        {lightboxIndex !== null && (
          <Lightbox photos={sortedPhotos} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onNavigate={setLightboxIndex} onToggleLike={toggleLike} />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ===================== PAGE =====================

export default function PhotosPage() {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(() => {
    const action = searchParams.get('action');
    if (action === 'deposer') return 'upload';
    if (action === 'album') return getAuthToken() ? 'album' : 'login';
    return 'home';
  }); // home | upload | login | album

  const goAlbum = useCallback(() => {
    setView(getAuthToken() ? 'album' : 'login');
  }, []);

  const onAuthNeeded = useCallback(() => setView('login'), []);

  return (
    <PublicLayout>
      <div className="section-padding min-h-[70vh]" data-testid="photos-page">
        {view === 'home' && <HomeView onUpload={() => setView('upload')} onAlbum={goAlbum} />}
        {view === 'upload' && <UploadView onBack={() => setView('home')} onAlbum={goAlbum} />}
        {view === 'login' && (
          <div>
            <button onClick={() => setView('home')} className="flex items-center gap-2 text-stone hover:text-terracotta mb-6 text-sm mx-auto" data-testid="photos-login-back-btn">
              <ArrowLeft className="w-4 h-4" /> Retour
            </button>
            <div className="text-center mb-8">
              <h1 className="font-display text-3xl sm:text-4xl text-charcoal mb-3">
                L'album des invités <Heart className="inline w-7 h-7 text-terracotta fill-terracotta -mt-2" />
              </h1>
              <p className="text-stone max-w-md mx-auto">Indiquez simplement l'email avec lequel vous avez été invité pour consulter l'album.</p>
            </div>
            <AlbumAccessForm onSuccess={() => setView('album')} />
          </div>
        )}
        {view === 'album' && <AlbumView onBack={() => setView('home')} onUpload={() => setView('upload')} onAuthNeeded={onAuthNeeded} />}
      </div>
    </PublicLayout>
  );
}
